// Hash-based view router. Views register themselves; navigate() renders the
// active view into #app, keeps a back stack, AND syncs the browser URL so
// every page is shareable, deep-linkable, and works with back/forward.
//
// URL shape: #/<view>?key=value  (e.g. #/search, #/verb-detail?inf=correr)
const views = {};
const stack = [];

let mountEl = null;
let current = null;

export function initRouter(el) {
  mountEl = el;
  // Back/forward (and app-level navigateBack) land here: re-render the target
  // view from the popped history state, or fall back to parsing the URL hash.
  window.addEventListener("popstate", (e) => {
    const target = e.state && e.state.name ? e.state : parseHash();
    if (target && views[target.name]) {
      current = { name: target.name, params: target.params || {} };
      views[target.name](mountEl, current.params);
    }
  });
}

export function registerView(name, fn) {
  views[name] = fn;
}

function paramsToQuery(params) {
  const keys = Object.keys(params || {});
  if (!keys.length) return "";
  return (
    "?" +
    keys
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
      .join("&")
  );
}

function hashFor(name, params) {
  return `#/${name}${paramsToQuery(params)}`;
}

// Parse the current location hash back into { name, params }, or null if empty.
function parseHash() {
  const raw = window.location.hash.replace(/^#\/?/, "");
  if (!raw) return null;
  const [name, qs] = raw.split("?");
  const params = {};
  if (qs) {
    qs.split("&").forEach((pair) => {
      const [k, v] = pair.split("=");
      if (k) params[decodeURIComponent(k)] = decodeURIComponent(v || "");
    });
  }
  return { name, params };
}

export function navigate(name, params = {}, opts = {}) {
  const view = views[name];
  if (!view) {
    console.error(`Unknown view: ${name}`);
    return;
  }
  if (!opts.replace) stack.push({ name, params });
  current = { name, params };

  // Sync the browser URL. `replace` overwrites the current entry (used for the
  // initial route) so we don't leave a dead first step in browser history.
  const url = hashFor(name, params);
  if (opts.replace) window.history.replaceState({ name, params }, "", url);
  else window.history.pushState({ name, params }, "", url);

  view(mountEl, params);
}

export function reRenderCurrent() {
  if (current) {
    views[current.name]?.(mountEl, current.params);
  }
}

export function navigateBack(fallback = "search") {
  // Prefer real browser back so the URL and history pointer stay consistent;
  // the popstate handler renders the previous view. Fall back to a replace
  // navigation when there is nothing to go back to (e.g. a fresh deep link).
  if (stack.length > 1 && window.history.length > 1) {
    stack.pop();
    window.history.back();
  } else {
    stack.length = 0;
    navigate(fallback, {}, { replace: true });
  }
}

// Honor an existing URL hash on first load (deep links), else open `fallback`.
export function startRoute(fallback) {
  const route = parseHash();
  if (route && views[route.name]) navigate(route.name, route.params, { replace: true });
  else navigate(fallback, {}, { replace: true });
}

export function currentView() {
  return current;
}
